import { addResponseStatusToSpan } from '@vs-shop/engine/utils/httpTracing';
import type { DateTime } from 'effect';
import { Effect, Request, RequestResolver } from 'effect';

import type { ApiInfrastructureErrors } from '@vs-shop/engine/data/error/api';
import type { BackendApiErrorUnion } from '@vs-shop/engine/data/error/backendApi/_internal/createBackendApiError';
import { createBackendApiError } from '@vs-shop/engine/data/error/backendApi/_internal/createBackendApiError';
import { liftRequest } from '@vs-shop/engine/request/backendApi/context';
import type { ProductFavouriteListModifyProductsInputProductVariantCodes } from '../../../schema/openapi/backend';
import { BackendApi } from '../../../service/api/BackendApi';
import type { AuthToken } from '../../../service/auth/AuthToken';
import type { LoggerConfig } from '@vs-shop/engine/service/logger/LoggerConfig';
import { ApiModule } from '@vs-shop/engine/service/logger/ApiModule';
import { logApiOperation } from '@vs-shop/engine/utils/logger/apiLogger';
import type * as HttpClient from 'effect/unstable/http/HttpClient';
import * as HttpClientResponse from 'effect/unstable/http/HttpClientResponse';

const ENDPOINT = '/api/v2/shop/product-favorites/list/{uuid}/products/remove';
const HTTP_METHOD = 'post';

export const DeleteProductsFromFavouriteListError = createBackendApiError({
  domain: 'ProductFavouriteList',
  endpoint: 'DeleteProducts',
  path: ENDPOINT,
  identifierField: 'uuid',
} as const);

export type DeleteProductsFromFavouriteListInput = {
  uuid: string;
  productVariantCodes: ProductFavouriteListModifyProductsInputProductVariantCodes;
};

type DeleteProductsFromFavouriteListError = BackendApiErrorUnion<
  typeof DeleteProductsFromFavouriteListError
>;

const deleteProductsFromFavouriteListFetch = ({
  uuid,
  productVariantCodes,
}: DeleteProductsFromFavouriteListInput) =>
  Effect.gen(function* () {
    const apiClient = yield* BackendApi;
    return yield* apiClient
      .path(ENDPOINT)
      .method(HTTP_METHOD)
      .accept('application/json', 'application/json')
      .fetch({
        params: { uuid },
        body: { productVariantCodes },
      });
  }).pipe(
    Effect.withSpan(`http.${HTTP_METHOD} ${ENDPOINT}`, {
      kind: 'client',
      attributes: {
        'http.request.method': HTTP_METHOD,
        'productFavouriteList.uuid': uuid,
      },
    })
  );

const deleteProductsFromFavouriteList = ({
  uuid,
  productVariantCodes,
}: DeleteProductsFromFavouriteListInput) =>
  deleteProductsFromFavouriteListFetch({ uuid, productVariantCodes }).pipe(
    Effect.map((response) => response.response),
    Effect.tap(addResponseStatusToSpan),
    Effect.flatMap(
      HttpClientResponse.matchStatus({
        '2xx': Effect.succeed,
        403: (response) =>
          Effect.fail(
            new DeleteProductsFromFavouriteListError.ApiRequestError({
              uuid,
              response,
            })
          ),
        404: (response) =>
          Effect.fail(new DeleteProductsFromFavouriteListError.NotFoundError({ uuid, response })),
        orElse: (response) =>
          Effect.fail(
            new DeleteProductsFromFavouriteListError.ApiRequestError({
              uuid,
              response,
            })
          ),
      })
    ),
    Effect.tap(() =>
      logApiOperation(
        ApiModule.ProductFavouriteListProductsDelete,
        'Debug',
        `Successfully deleted products from favourite list: ${uuid}`
      )
    ),
    Effect.withSpan('api.productFavouriteList.deleteProducts', {
      kind: 'internal',
      attributes: {
        'productFavouriteList.uuid': uuid,
      },
    })
  );

type DeleteProductsFromFavouriteListRequest = {
  readonly _tag: 'DeleteProductsFromFavouriteListRequest';
  readonly uuid: string;
  readonly productVariantCodes: ProductFavouriteListModifyProductsInputProductVariantCodes;
} & Request.Request<
  void,
  DeleteProductsFromFavouriteListError | ApiInfrastructureErrors,
  BackendApi | AuthToken | HttpClient.HttpClient | LoggerConfig | DateTime.CurrentTimeZone
>;

const DeleteProductsFromFavouriteListRequest =
  Request.tagged<DeleteProductsFromFavouriteListRequest>('DeleteProductsFromFavouriteListRequest');

const DeleteProductsFromFavouriteListResolver = RequestResolver.fromEffect(
  liftRequest((request: DeleteProductsFromFavouriteListRequest) =>
    deleteProductsFromFavouriteList({
      uuid: request.uuid,
      productVariantCodes: request.productVariantCodes,
    })
  )
);

export const requestDeleteProductsFromFavouriteList = ({
  uuid,
  productVariantCodes,
}: DeleteProductsFromFavouriteListInput) =>
  Effect.request(
    DeleteProductsFromFavouriteListRequest({ uuid, productVariantCodes }),
    DeleteProductsFromFavouriteListResolver
  );
